import { CampaignPulseCard } from '../../../../components/Summary/CampaignPulseCard';
import { HeroMetricProps } from '../../../../components/Summary/HeroMetric';
import { CpgReport } from '../../api/schema';
import { faEye, faUser, faShuffle } from '@fortawesome/pro-regular-svg-icons';
import { faDollar } from '@fortawesome/pro-solid-svg-icons';
import { Stack, Typography } from '@mui/material';
import { formatters } from '@utils/formattersV2';
import { SummaryCard, SummaryCardProps } from './SummaryCard';
import { useFeatures } from '@utils/hooks';
import { memo } from 'react';

export type SummaryProps = {
  report: SummaryCardProps['report'] &
    Pick<
      CpgReport,
      | 'incrementalSalesLiftConfidenceLevel'
      | 'matchedForAttribution'
      | 'impressions'
      | 'retailerBanners'
      | 'incrementalSalesRate'
      | 'totalSalesRate'
    >;
  allReports: SummaryCardProps['allReports'];
  campaign: SummaryCardProps['campaign'];
};

export const Summary = memo((props: SummaryProps) => {
  const features = useFeatures();

  const incrementalOutcomeMetrics: HeroMetricProps[] = [
    {
      value: props.report.incrementalSalesLift,
      valueFormatter: formatters.lift,
      confidenceLevel: props.report.incrementalSalesLiftConfidenceLevel,
      label: 'Incremental Sales Lift',
      colorScheme: 'sales',
      icon: faDollar,
      ['data-testid']: 'incremental-sales-lift-outcome',
    },
    {
      value: props.report.projectedIncrementalSales,
      valueFormatter: formatters.currency,
      label: 'Projected Incremental Sales',
      colorScheme: 'sales',
      icon: faDollar,
      ['data-testid']: 'projected-incrementa-sales-outcome',
    },
    ...(features.cpgAdditionalMetrics
      ? [
          {
            value: props.report.incrementalSalesRate,
            valueFormatter: formatters.currency,
            label: 'Incremental Dollars per User',
            colorScheme: 'sales' as const,
            icon: faDollar,
            ['data-testid']: 'incremental-dollars-per-user',
          },
        ]
      : []),
  ];

  const mediaAttributedMetrics: HeroMetricProps[] = [
    {
      value: props.report.totalProjectedSales,
      valueFormatter: formatters.currency,
      label: 'Total Projected Sales',
      colorScheme: 'sales',
      icon: faDollar,
      size: 'sm',
      ['data-testid']: 'total-projected-sales-outcome',
    },
    ...(features.cpgAdditionalMetrics
      ? [
          {
            value: props.report.totalSalesRate,
            valueFormatter: formatters.currency,
            label: 'Dollars per User',
            colorScheme: 'sales' as const,
            icon: faDollar,
            size: 'sm' as const,
            ['data-testid']: 'dollars-per-user',
          },
        ]
      : []),
  ];

  const mediaInputMetrics: HeroMetricProps[] = [
    {
      value: props.report.impressions,
      valueFormatter: formatters.impressions,
      label: 'Impressions',
      colorScheme: 'plain',
      icon: faEye,
      size: 'sm',
      ['data-testid']: 'imprssions-outcome',
    },
    {
      value: props.report.uniqueUsers,
      valueFormatter: formatters.users,
      label: 'Unique Users',
      colorScheme: 'plain',
      icon: faUser,
      size: 'sm',
      ['data-testid']: 'unique-users-outcome',
    },
    {
      value: props.report.matchedForAttribution,
      valueFormatter: formatters.users,
      label: 'Matched For Attribution',
      colorScheme: 'plain',
      icon: faShuffle,
      size: 'sm',
      ['data-testid']: 'matched-for-attribution-outcome',
    },
    {
      value: props.report.retailerBanners,
      valueFormatter: formatters.number,
      label: 'Unique Retailer Banners',
      colorScheme: 'plain',
      icon: faShuffle,
      size: 'sm',
      ['data-testid']: 'reatiler-banners-outcome',
    },
  ];

  return (
    <Stack direction="column" gap="1rem" flexWrap="wrap">
      <CampaignPulseCard
        incrementalOutcomeMetrics={incrementalOutcomeMetrics}
        mediaAttributedMetrics={mediaAttributedMetrics}
        mediaInputMetrics={mediaInputMetrics}
      >
        <Typography>
          Get a highlight of your campaign outcomes and input metrics. Deep dive
          into performance by retailer and real-time optimization.
        </Typography>
      </CampaignPulseCard>

      <SummaryCard
        report={props.report}
        allReports={props.allReports}
        campaign={props.campaign}
      />
    </Stack>
  );
});

Summary.displayName = 'Summary';
